import React, { useState } from 'react';
import { REPORT } from '../print/PrintAtoms';
import type { ParcelGrade } from '../../lib/api';

export interface GradeCardPalette {
  heading: string;
  secondary: string;
  tertiary: string;
  rule: string;
}

const DEFAULT_PALETTE: GradeCardPalette = {
  heading: REPORT.heading,
  secondary: REPORT.secondary,
  tertiary: REPORT.tertiary,
  rule: REPORT.rule,
};

// Fixed right-skewed distribution shape (decorative, not data-driven)
const HISTOGRAM_BARS = (() => {
  const bins = 20;
  const data: number[] = [];
  for (let i = 0; i < bins; i++) {
    const x = (i + 0.5) / bins;
    const v = Math.exp(-Math.pow((x - 0.4) / 0.22, 2));
    data.push(v);
  }
  const max = Math.max(...data);
  return data.map(v => v / max);
})();

// SVG percentile histogram — shows a bell-curve-ish distribution with a "you are here" marker
const PercentileHistogram: React.FC<{ score: number; color: string; palette: GradeCardPalette }> = ({ score, color, palette }) => {
  const W = 200, H = 48;
  const PAD = { left: 4, right: 4, top: 4, bottom: 14 };
  const plotW = W - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;
  const bars = HISTOGRAM_BARS;

  const barW = plotW / bars.length - 1;
  const markerX = PAD.left + (score / 100) * plotW;
  const bars_count = bars.length;

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display: 'block', marginTop: 4 }}>
      {/* Bars */}
      {bars.map((v, i) => {
        const x = PAD.left + (i / bars_count) * plotW;
        const barH = v * plotH * 0.85;
        const barScore = ((i + 0.5) / bars_count) * 100;
        const isHighlighted = Math.abs(barScore - score) < (100 / bars_count / 2);
        return (
          <rect
            key={i}
            x={x} y={PAD.top + plotH - barH}
            width={barW} height={barH}
            rx={1}
            fill={isHighlighted ? color : palette.rule}
            opacity={isHighlighted ? 1 : 0.6}
          />
        );
      })}
      {/* Marker line */}
      <line x1={markerX} y1={PAD.top - 2} x2={markerX} y2={PAD.top + plotH} stroke={color} strokeWidth={2} />
      <circle cx={markerX} cy={PAD.top - 2} r={2.5} fill={color} />
      {/* X-axis labels */}
      <text x={PAD.left} y={H - 2} fontSize={6} fill={palette.tertiary}>0</text>
      <text x={PAD.left + plotW / 2} y={H - 2} fontSize={6} fill={palette.tertiary} textAnchor="middle">Score</text>
      <text x={PAD.left + plotW} y={H - 2} fontSize={6} fill={palette.tertiary} textAnchor="end">100</text>
      {/* "You" label */}
      <text x={markerX} y={PAD.top + plotH + 10} fontSize={6.5} fill={color} fontWeight={700} textAnchor="middle">
        You: {score}
      </text>
    </svg>
  );
};

function computeTopLabel(percentile: number): string {
  const topPercent = 100 - percentile;
  return topPercent <= 0 && percentile > 50 ? '<1' : String(topPercent);
}

// Methodology content — exported so it can be rendered externally at full width
export const GradeMethodology: React.FC<{
  methodology: { inputs: { name: string; value: string; weight: string }[]; thresholds: { grade: string; requirement: string }[] };
  grade: ParcelGrade;
  label: string;
  color: string;
  region: string;
  totalParcels: number;
  palette?: GradeCardPalette;
}> = ({ methodology, grade, label, color, region, totalParcels, palette = DEFAULT_PALETTE }) => {
  const topLabel = computeTopLabel(grade.percentile);

  return (
    <div style={{
      marginTop: '8px', padding: '10px 12px',
      background: color + '0a',
      borderRadius: '6px',
      borderLeft: `3px solid ${color}`,
    }}>
      <div style={{ fontSize: '9px', fontWeight: 600, color, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px', fontFamily: 'monospace' }}>
        {label}
      </div>

      <div style={{ fontSize: '8px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.08em', color: palette.heading, marginBottom: '4px', fontFamily: 'monospace' }}>
        INPUTS
      </div>
      {methodology.inputs.map((input) => (
        <div key={input.name} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', padding: '1px 0' }}>
          <span style={{ color: palette.secondary }}>{input.name} ({input.weight})</span>
          <span style={{ fontWeight: 500, color: palette.heading }}>{input.value}</span>
        </div>
      ))}

      <div style={{ fontSize: '8px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.08em', color: palette.heading, marginTop: '6px', marginBottom: '4px', fontFamily: 'monospace' }}>
        THRESHOLDS
      </div>
      {methodology.thresholds.map((t) => (
        <div key={t.grade} style={{ display: 'flex', gap: '6px', fontSize: '10px', padding: '1px 0' }}>
          <span style={{ fontWeight: 500, color, minWidth: '18px' }}>{t.grade}</span>
          <span style={{ color: palette.secondary }}>{t.requirement}</span>
        </div>
      ))}

      <div style={{ fontSize: '8px', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.08em', color: palette.heading, marginTop: '6px', marginBottom: '2px', fontFamily: 'monospace' }}>
        DISTRIBUTION
      </div>
      <PercentileHistogram score={grade.score} color={color} palette={palette} />
      <div style={{ fontSize: '9px', color: palette.secondary, marginTop: '4px' }}>
        Raw score: {grade.score}/100{region === 'Assembly' ? '' : ` · Rank: top ${topLabel}% of ${totalParcels.toLocaleString()} sites`}
      </div>
    </div>
  );
};

export interface GradeCardProps {
  grade: ParcelGrade;
  label: string;
  color: string;
  region: string;
  totalParcels: number;
  methodology: {
    inputs: { name: string; value: string; weight: string }[];
    thresholds: { grade: string; requirement: string }[];
  };
  palette?: GradeCardPalette;
  expanded?: boolean;
  onToggle?: () => void;
  hideExpansion?: boolean; // suppress internal methodology — parent renders it externally
}

export const GradeCard: React.FC<GradeCardProps> = ({ grade, label, color, region, totalParcels, methodology, palette = DEFAULT_PALETTE, expanded: controlledExpanded, onToggle, hideExpansion }) => {
  const [internalExpanded, setInternalExpanded] = useState(false);
  const expanded = controlledExpanded !== undefined ? controlledExpanded : internalExpanded;
  const toggle = onToggle || (() => setInternalExpanded(!internalExpanded));

  const topLabel = computeTopLabel(grade.percentile);

  return (
    <div style={{
      background: color + '18',
      borderRadius: '6px',
      padding: '12px 14px',
    }}>
      <div style={{ fontSize: '32px', fontWeight: 400, color, lineHeight: 1, fontFamily: 'Georgia, serif' }}>
        {grade.grade}
      </div>
      <div style={{
        fontSize: '11px', fontWeight: 500, color,
        textTransform: 'uppercase', letterSpacing: '0.1em', marginTop: '3px',
        fontFamily: 'monospace',
      }}>
        {label}
      </div>
      <div style={{ fontSize: '10px', color: palette.secondary, marginTop: '3px' }}>
        {/* Synthetic assembly grades (region 'Assembly') have no assembly-aware
            percentile — the score is the honest number, so show it instead of a
            misleading cross-parcel rank. Single-parcel grades keep their rank. */}
        {region === 'Assembly' ? `Score ${grade.score}/100` : `Top ${topLabel}% of ${region}`}
      </div>
      <div
        className="briefing-no-print"
        onClick={toggle}
        style={{
          fontSize: '9px', color: palette.secondary,
          cursor: 'pointer', marginTop: '5px',
        }}
      >
        How we scored this {expanded ? '\u2191' : '\u2193'}
      </div>

      {expanded && !hideExpansion && (
        <GradeMethodology
          grade={grade} label={label} color={color}
          region={region} totalParcels={totalParcels}
          methodology={methodology} palette={palette}
        />
      )}
    </div>
  );
};
